import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useSocketEventListener } from '../index';
import {
    addIncomingMessage,
    setAgentTyping,
    onSuccessUpdateStatus,
    markMessageFailed,
    markMessageQueued,
    patchMessageAttachments
} from '../../store/live-chat/slice';
import { callLiveChatConversations } from '../../store/live-chat/sagas-actions';

const useLiveChat = () => {
    const dispatch = useDispatch();
    const typingTimeoutRef = useRef(null);

    const onNewMessage = (data) => {
        // BE emits nested { conversation_id, message: {...} } post-JSM migration.
        // Legacy flat shape { conversation_id, chat_message_id, ... } is still handled.
        const flat = data?.message
            ? { conversation_id: data.conversation_id, ...data.message }
            : data;
        dispatch(addIncomingMessage(flat));
    };

    const onAgentTyping = () => {
        dispatch(setAgentTyping(true));
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
            dispatch(setAgentTyping(false));
        }, 3000);
    };

    const onConversationUpdate = () => {
        dispatch(callLiveChatConversations());
    };

    const onStatusUpdate = (data) => {
        dispatch(onSuccessUpdateStatus({
            conversationId: data.conversation_id,
            status: data.status,
            updated_date: data.updated_date
        }));
    };

    const onDeliveryFailed = (data) => {
        dispatch(markMessageFailed({
            conversation_id: data.conversation_id,
            message_id: data.message_id,
            reason: data.reason
        }));
    };

    const onDeliveryWarning = (data) => {
        dispatch(markMessageQueued({ conversation_id: data.conversation_id }));
    };

    const onAttachmentReady = (data) => {
        dispatch(patchMessageAttachments({
            conversation_id: data.conversation_id,
            message_id: data.message_id,
            attachments: data.attachments
        }));
    };

    useSocketEventListener({
        socketModuleName: 'live_chat',
        socketEventName: 'LIVE_CHAT_NEW_MESSAGE',
        listenOnly: false,
        customHandler: onNewMessage
    });

    useSocketEventListener({
        socketModuleName: 'live_chat',
        socketEventName: 'LIVE_CHAT_TYPING',
        listenOnly: true,
        customHandler: onAgentTyping
    });

    useSocketEventListener({
        socketModuleName: 'live_chat',
        socketEventName: 'LIVE_CHAT_CONVERSATION_UPDATE',
        listenOnly: true,
        customHandler: onConversationUpdate
    });

    useSocketEventListener({
        socketModuleName: 'live_chat',
        socketEventName: 'LIVE_CHAT_STATUS_CHANGED',
        listenOnly: true,
        customHandler: onStatusUpdate
    });

    useSocketEventListener({
        socketModuleName: 'live_chat',
        socketEventName: 'LIVE_CHAT_DELIVERY_FAILED',
        listenOnly: true,
        customHandler: onDeliveryFailed
    });

    useSocketEventListener({
        socketModuleName: 'live_chat',
        socketEventName: 'LIVE_CHAT_DELIVERY_WARNING',
        listenOnly: true,
        customHandler: onDeliveryWarning
    });

    useSocketEventListener({
        socketModuleName: 'live_chat',
        socketEventName: 'LIVE_CHAT_ATTACHMENT_READY',
        listenOnly: true,
        customHandler: onAttachmentReady
    });

    useEffect(() => {
        dispatch(callLiveChatConversations());
        return () => {
            clearTimeout(typingTimeoutRef.current);
        };
    }, []);
};

export default useLiveChat;
